import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Search, Gift, Clock, Loader2, Rocket, Sparkles, ExternalLink, CheckCircle2, AlertCircle, Mail, Tag, CreditCard, Zap } from "lucide-react";
import { toast } from "sonner";

function defaultExpiry() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setHours(23, 59, 0, 0);
  // format for datetime-local input: YYYY-MM-DDTHH:mm
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtExpiry(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [credits, setCredits] = useState(30);
  const [expiry, setExpiry] = useState(defaultExpiry());
  const [granting, setGranting] = useState(false);
  // AI Funnel Push (Systeme.io) — admin only
  const [pushUser, setPushUser] = useState(null);
  const [pushCampaigns, setPushCampaigns] = useState([]);
  const [campLoading, setCampLoading] = useState(false);
  const [pushCampId, setPushCampId] = useState("");
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  useEffect(() => {
    if (ready && user && !user.is_admin) navigate("/dashboard");
  }, [ready, user, navigate]);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q: query || "" } });
      setUsers(data.users || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user?.is_admin) load(""); }, [user, load]);

  const grant = async () => {
    if (!selected) return;
    const n = parseInt(credits, 10);
    if (!n || n <= 0) { toast.error("Enter a positive number of credits"); return; }
    setGranting(true);
    try {
      const iso = new Date(expiry).toISOString();
      const { data } = await api.post("/admin/grant-credits", {
        email: selected.email, credits: n, expires_at: iso,
      });
      toast.success(`Granted ${data.credits_added} pilot credits to ${data.email} (expires ${fmtExpiry(data.pilot_expires_at)})`);
      setSelected(null);
      load(q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Grant failed");
    } finally {
      setGranting(false);
    }
  };

  if (!user?.is_admin) return null;

  const openPush = async (u) => {
    setPushUser(u); setSelected(null); setPushResult(null); setPushCampId(""); setPushCampaigns([]);
    setCampLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/campaigns`);
      setPushCampaigns(data.campaigns || []);
      if ((data.campaigns || []).length) setPushCampId(data.campaigns[0].id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't load campaigns");
    } finally { setCampLoading(false); }
  };

  const doPush = async () => {
    if (!pushUser || !pushCampId) return;
    setPushing(true); setPushResult(null);
    try {
      const { data } = await api.post("/admin/systeme-push", { user_id: pushUser.id, campaign_id: pushCampId });
      const jobId = data.job_id;
      // Poll — 3 AI page generations take up to ~90s.
      const started = Date.now();
      const poll = async () => {
        const { data: s } = await api.get(`/admin/systeme-push/${jobId}`);
        if (s.status === "done") {
          setPushResult(s.result);
          setPushing(false);
          toast.success(s.result?.page_fill === "filled"
            ? "Funnel pushed — pages auto-designed by AI!"
            : "Funnel pushed (some pages fell back to copy-paste)");
          return;
        }
        if (s.status === "error") {
          setPushing(false);
          toast.error(s.error || "Push failed");
          return;
        }
        if (Date.now() - started > 300000) {
          setPushing(false);
          toast.error("Push is taking longer than expected — check your Systeme account.");
          return;
        }
        setTimeout(poll, 4000);
      };
      setTimeout(poll, 4000);
    } catch (e) {
      setPushing(false);
      toast.error(formatApiErrorDetail(e) || "Push failed");
    }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-page">
      <div className="mb-8">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <ShieldCheck size={24} />
        </div>
        <h1 className="font-head text-4xl font-bold">Admin</h1>
        <p className="mt-2 text-muted-foreground">
          Grant pilot / promotional credits to a user. Pilot credits expire on the date you set; paid credits never expire.
        </p>
      </div>

      {/* Search */}
      <Card className="mb-6 p-5">
        <Label className="mb-2 block">Find a user by email</Label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="admin-user-search" value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(q)}
              placeholder="e.g. jane@example.com" className="pl-9" />
          </div>
          <Button onClick={() => load(q)} data-testid="admin-search-btn" variant="secondary">Search</Button>
        </div>
      </Card>

      {/* Results */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-10 text-muted-foreground">
            <Loader2 size={18} className="animate-spin" /> Loading users…
          </div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="admin-no-users">No users found.</div>
        ) : (
          <div className="divide-y divide-border">
            {users.map((u) => (
              <div key={u.id} data-testid={`admin-user-row-${u.email}`}
                className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate font-semibold">{u.name || u.email}
                    {u.role === "admin" && <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">admin</span>}
                  </p>
                  <p className="truncate text-sm text-muted-foreground">{u.email}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Balance: <span className="font-semibold text-foreground">{u.credits}</span> credits
                    {u.pilot_credits > 0 && (
                      <span className="ml-2 inline-flex items-center gap-1 text-amber-600">
                        <Clock size={11} /> {u.pilot_credits} pilot · expires {fmtExpiry(u.pilot_expires_at)}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                  <Button size="sm" onClick={() => { setSelected(u); setCredits(30); setExpiry(defaultExpiry()); setPushUser(null); }}
                    data-testid={`admin-grant-open-${u.email}`}
                    className="gap-1.5 rounded-full">
                    <Gift size={15} /> Grant pilot credits
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openPush(u)}
                    data-testid={`admin-push-open-${u.email}`}
                    className="gap-1.5 rounded-full border-accent/40 text-accent hover:bg-accent/10">
                    <Rocket size={15} /> AI Funnel Push
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Grant form */}
      {selected && (
        <Card className="mt-6 border-primary/30 p-6" data-testid="admin-grant-form">
          <h2 className="font-head text-xl font-bold">Grant pilot credits</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            To <span className="font-semibold text-foreground">{selected.email}</span>
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Number of credits</Label>
              <Input data-testid="admin-grant-credits" type="number" min="1" value={credits}
                onChange={(e) => setCredits(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Expiry date &amp; time</Label>
              <Input data-testid="admin-grant-expiry" type="datetime-local" value={expiry}
                onChange={(e) => setExpiry(e.target.value)} />
              <p className="text-xs text-muted-foreground">Defaults to 7 days from now. Credits vanish after this.</p>
            </div>
          </div>
          <div className="mt-5 flex gap-2">
            <Button onClick={grant} disabled={granting} data-testid="admin-grant-submit"
              className="gap-1.5 rounded-full bg-primary hover:bg-primary/90">
              {granting ? <Loader2 size={16} className="animate-spin" /> : <Gift size={16} />}
              Grant credits
            </Button>
            <Button variant="ghost" onClick={() => setSelected(null)} data-testid="admin-grant-cancel">Cancel</Button>
          </div>
        </Card>
      )}

      {/* AI Funnel Push (Systeme.io) — admin only */}
      {pushUser && (
        <Card className="mt-6 border-accent/40 p-6" data-testid="admin-systeme-push-form">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-accent" />
            <h2 className="font-head text-xl font-bold">AI Funnel Push → Systeme.io</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Pushes <span className="font-semibold text-foreground">{pushUser.email}</span>'s completed campaign into{" "}
            <span className="font-semibold text-foreground">your</span> Systeme.io account with AI-designed opt-in, sales &amp; thank-you pages.
          </p>

          {campLoading ? (
            <div className="mt-4 flex items-center gap-2 text-muted-foreground">
              <Loader2 size={16} className="animate-spin" /> Loading completed campaigns…
            </div>
          ) : pushCampaigns.length === 0 ? (
            <div className="mt-4 rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground" data-testid="admin-push-no-campaigns">
              This user has no fully-completed campaigns yet (needs both a generated course and content pack).
            </div>
          ) : (
            <>
              <div className="mt-4 space-y-2">
                <Label>Choose a completed campaign</Label>
                <select data-testid="admin-push-campaign-select" value={pushCampId}
                  onChange={(e) => setPushCampId(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                  {pushCampaigns.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.topic ? ` — ${c.topic}` : ""}</option>
                  ))}
                </select>
              </div>
              <div className="mt-5 flex gap-2">
                <Button onClick={doPush} disabled={pushing} data-testid="admin-push-submit"
                  className="gap-1.5 rounded-full bg-accent text-white hover:bg-accent/90">
                  {pushing ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
                  {pushing ? "Designing & pushing…" : "Push to my Systeme (AI pages)"}
                </Button>
                <Button variant="ghost" onClick={() => { setPushUser(null); setPushResult(null); }} data-testid="admin-push-cancel">Close</Button>
              </div>
              {pushing && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Generating 3 AI-designed pages — this can take up to a minute.
                </p>
              )}
            </>
          )}

          {pushResult && (
            <div className="mt-6 space-y-3 border-t border-border pt-5" data-testid="admin-push-result">
              <div className="flex items-center gap-2">
                {pushResult.page_fill === "filled"
                  ? <CheckCircle2 size={18} className="text-emerald-600" />
                  : <AlertCircle size={18} className="text-amber-600" />}
                <p className="font-semibold">
                  Funnel “{pushResult.funnel?.name}” created — pages {pushResult.page_fill === "filled" ? "auto-designed by AI" : "fell back to copy-paste"}
                </p>
              </div>
              <div className="space-y-1.5">
                {(pushResult.page_results || []).map((p) => (
                  <div key={p.type} className="rounded-lg border border-border p-3 text-sm" data-testid={`admin-push-page-${p.type}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{p.page}</span>
                      <span className={p.status === "filled" ? "font-semibold text-emerald-600" : "font-semibold text-amber-600"}>{p.status}</span>
                    </div>
                    {p.url && (
                      <a href={p.url} target="_blank" rel="noopener noreferrer"
                        className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary hover:underline">
                        <ExternalLink size={11} /> {p.url}
                      </a>
                    )}
                    {p.error && <p className="mt-1 text-xs text-muted-foreground">⚠ {p.error}</p>}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                {pushResult.newsletter && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1"><Mail size={11} /> Newsletter draft</span>}
                {pushResult.tag && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1"><Tag size={11} /> {pushResult.tag.name}</span>}
                {pushResult.product && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1"><CreditCard size={11} /> Product · {pushResult.product.plans} plans</span>}
                {pushResult.automation && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1"><Zap size={11} /> Automation rule</span>}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
